import { cloneElement, useEffect, useRef, useState } from "react";
import { Plus, FileDoc, ToggleRight, ToggleLeft, PencilSimple, Tag } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import CatalogueProductPicker from "../lib/CatalogueProductPicker";
import RichTextEditor from "../components/RichTextEditor";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const PRODUCT_TYPE_FROM_KIND = (kind) => (kind === "service" ? "Recurring" : "One-time");

const blankLine = () => ({
    product_name: "",
    product_type: "One-time",
    amount: 0,
    amc_amount: 0,
    billing_frequency: "Annual",
    gst_percent: 18,
    hsn_sac_code: "997331",
    catalogue_id: null,
});

const blank = {
    code: "",
    name: "",
    description: "",
    header_text: "",
    line_items: [blankLine()],
};

function TemplateDialog({ template, onSaved, children }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank);
    const [submitting, setSubmitting] = useState(false);
    const isEdit = !!template;
    const baselineRef = useRef(JSON.stringify(blank));

    useEffect(() => {
        if (open) {
            let next;
            if (template) {
                next = {
                    code: template.code,
                    name: template.name,
                    description: template.description || "",
                    header_text: template.header_text || "",
                    line_items: (template.line_items || []).map((l) => ({ ...l })),
                };
            } else {
                next = blank;
            }
            setForm(next);
            baselineRef.current = JSON.stringify(next);
        }
    }, [open, template]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const updateLine = (idx, key, value) =>
        setForm((p) => ({ ...p, line_items: p.line_items.map((l, i) => (i === idx ? { ...l, [key]: value } : l)) }));
    const updateLineMany = (idx, patch) =>
        setForm((p) => ({ ...p, line_items: p.line_items.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
    const addLine = () =>
        setForm((p) => ({ ...p, line_items: [...p.line_items, blankLine()] }));
    const removeLine = (idx) =>
        setForm((p) => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));

    // Iter 39: catalogue pick → auto-fill name, type, fees, GST, HSN/SAC.
    // Operator can still override any field after pick.
    const handleCataloguePick = (idx, name, item) => {
        if (!item) {
            // Free-text / "Others" branch — preserve catalogue linkage clearing
            updateLineMany(idx, { product_name: name, catalogue_id: null });
            return;
        }
        updateLineMany(idx, {
            product_name: name,
            catalogue_id: item.id,
            hsn_sac_code: item.hsn_sac_code || "997331",
            gst_percent: item.default_gst_percent ?? 18,
            // Snapshot fee defaults — only fill if line is fresh (untouched).
            // Avoid overwriting deliberate operator-entered amounts.
            ...(Number(form.line_items[idx]?.amount || 0) === 0
                ? { amount: Number(item.default_software_fee || 0) }
                : {}),
            ...(Number(form.line_items[idx]?.amc_amount || 0) === 0
                ? { amc_amount: Number(item.default_amc_fee || 0) }
                : {}),
            // Pick a sensible product_type based on catalogue kind, only if
            // current value is the default "One-time".
            ...(form.line_items[idx]?.product_type === "One-time"
                ? { product_type: PRODUCT_TYPE_FROM_KIND(item.kind) }
                : {}),
        });
    };

    const submit = async () => {
        if (!form.name || form.line_items.length === 0 || form.line_items.some((l) => !l.product_name)) {
            toast.error("Name + at least one valid line item required");
            return;
        }
        setSubmitting(true);
        try {
            const lines = form.line_items.map((l) => ({
                product_name: l.product_name,
                product_type: l.product_type,
                amount: Number(l.amount) || 0,
                amc_amount: Number(l.amc_amount) || 0,
                billing_frequency: l.billing_frequency || "Annual",
                gst_percent: Number(l.gst_percent),
                gst_applicable: l.gst_applicable !== false,
                hsn_sac_code: (l.hsn_sac_code || "").trim() || "997331",
                catalogue_id: l.catalogue_id || null,
            }));
            if (isEdit) {
                await api.patch(`/billing/proposal-template/${template.id}`, {
                    name: form.name, description: form.description,
                    header_text: form.header_text, line_items: lines,
                });
                toast.success("Template updated");
            } else {
                await api.post("/billing/proposal-template", {
                    code: form.code, name: form.name, description: form.description,
                    header_text: form.header_text, line_items: lines,
                });
                toast.success("Template created");
            }
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            setOpen(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {cloneElement(children, { onClick: () => setOpen(true) })}
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                contentClassName="max-w-2xl"
                testId="proposal-template-dialog"
                title={isEdit ? "Edit template" : "New proposal template"}
                description="Templates pre-fill the proposal header text and line items. Edits don't apply retroactively to existing proposals."
            >
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Code</Label>
                        <Input disabled={isEdit} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. saas_annual_bundle" className="mt-1.5 font-mono" data-testid="tpl-code-input" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Display name</Label>
                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="SaaS — annual bundle" className="mt-1.5" data-testid="tpl-name-input" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Description</Label>
                    <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="tpl-desc-input" />
                </div>
                <div>
                    <Label className="text-xs font-semibold">Header text</Label>
                    <div className="mt-1.5">
                        <RichTextEditor
                            value={form.header_text}
                            onChange={(html) => setForm({ ...form, header_text: html })}
                            minHeight={180}
                            testId="tpl-header-input"
                        />
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Line items</Label>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                        Pick from the Products &amp; Services master to auto-fill GST / HSN / fees / type.
                        A frozen catalogue snapshot is kept on each line for audit immutability.
                    </p>
                    <div className="mt-1.5 space-y-4">
                        {form.line_items.map((l, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50/40" data-testid={`tpl-line-row-${idx}`}>
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <Label className="text-[10px] reedos-eyebrow text-slate-500">Product / service</Label>
                                        <div className="mt-1">
                                            <CatalogueProductPicker
                                                value={l.product_name}
                                                onChange={(v, item) => handleCataloguePick(idx, v, item)}
                                                testidPrefix={`tpl-line-${idx}`}
                                                required
                                            />
                                        </div>
                                        {l.catalogue_id && (
                                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700 font-mono" data-testid={`tpl-line-cat-snapshot-${idx}`}>
                                                <Tag size={10} weight="duotone" /> Snapshot · {l.catalogue_id}
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        type="button" size="icon" variant="ghost"
                                        disabled={form.line_items.length === 1}
                                        onClick={() => removeLine(idx)}
                                        className="h-9 w-9 text-slate-400 hover:text-rose-600 shrink-0"
                                        data-testid={`tpl-line-remove-${idx}`}
                                        title="Remove line"
                                    >×</Button>
                                </div>
                                <div className="grid grid-cols-12 gap-2 items-start">
                                    <div className="col-span-3">
                                        <Label className="text-[10px] reedos-eyebrow text-slate-500">Type</Label>
                                        <select
                                            className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded-md bg-white"
                                            value={l.product_type}
                                            onChange={(e) => updateLine(idx, "product_type", e.target.value)}
                                            data-testid={`tpl-line-type-${idx}`}
                                        >
                                            <option>One-time</option>
                                            <option>Recurring</option>
                                            <option>AMC</option>
                                        </select>
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-[10px] reedos-eyebrow text-slate-500">Software fee (₹)</Label>
                                        <Input
                                            className="mt-1 font-mono text-right h-9"
                                            type="number" min="0" step="0.01"
                                            value={l.amount}
                                            onChange={(e) => updateLine(idx, "amount", e.target.value)}
                                            data-testid={`tpl-line-amount-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-[10px] reedos-eyebrow text-slate-500">AMC fee (₹/yr)</Label>
                                        <Input
                                            className="mt-1 font-mono text-right h-9"
                                            type="number" min="0" step="0.01"
                                            value={l.amc_amount ?? 0}
                                            onChange={(e) => updateLine(idx, "amc_amount", e.target.value)}
                                            placeholder="0"
                                            data-testid={`tpl-line-amc-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Label className="text-[10px] reedos-eyebrow text-slate-500">GST %</Label>
                                        <Input
                                            className="mt-1 font-mono text-right h-9"
                                            type="number" min="0" max="28" step="1"
                                            value={l.gst_percent}
                                            onChange={(e) => updateLine(idx, "gst_percent", e.target.value)}
                                            data-testid={`tpl-line-gst-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] reedos-eyebrow text-slate-500">HSN / SAC</Label>
                                        <Input
                                            className="mt-1 font-mono h-9"
                                            value={l.hsn_sac_code || ""}
                                            onChange={(e) => updateLine(idx, "hsn_sac_code", e.target.value.replace(/\s+/g, ""))}
                                            placeholder="997331"
                                            maxLength={20}
                                            data-testid={`tpl-line-hsn-${idx}`}
                                        />
                                    </div>
                                    {/* AMC frequency — shown only when AMC fee > 0 */}
                                    {Number(l.amc_amount) > 0 && (
                                        <div className="col-span-12 flex items-center gap-2 text-[11px] text-slate-500">
                                            <span className="reedos-eyebrow text-[9px]">AMC frequency</span>
                                            <select
                                                className="h-7 px-2 text-[11px] border border-slate-200 rounded bg-white"
                                                value={l.billing_frequency || "Annual"}
                                                onChange={(e) => updateLine(idx, "billing_frequency", e.target.value)}
                                                data-testid={`tpl-line-freq-${idx}`}
                                            >
                                                <option>Annual</option>
                                                <option>Half-Yearly</option>
                                                <option>Quarterly</option>
                                                <option>Monthly</option>
                                            </select>
                                            <span className="text-[10px] text-slate-400">— first AMC bill is auto-scheduled by the AMC engine after PO finalization.</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Button variant="outline" size="sm" type="button" onClick={addLine} className="mt-3 gap-1.5">
                        <Plus size={12} /> Add line
                    </Button>
                </div>
                <DialogFooter>
                    <FormDialogCancel testId="tpl-cancel-btn">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="tpl-submit-btn">
                        {submitting ? "Saving..." : (isEdit ? "Save changes" : "Create template")}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

export default function ProposalTemplates() {
    const { can } = useAuth();
    // Iter 66.4 — Sales has read-only access. canWrite gates write affordances
    // (new template, edit, activate/deactivate). Server enforces template:write
    // on every mutation endpoint independently.
    const canWrite = can("template:write");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/billing/proposal-template/list");
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load templates"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const toggleActive = async (t) => {
        try {
            await api.patch(`/billing/proposal-template/${t.id}`, { is_active: !t.is_active });
            toast.success(t.is_active ? "Template deactivated" : "Template activated");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    return (
        <div data-testid="proposal-templates-page">
            <PageHeader
                eyebrow={canWrite ? "Administration" : "Administration · Read-only"}
                title="Proposal templates"
                description={
                    canWrite
                        ? "Reusable header text + line-item bundles. Sales picks one when creating a proposal."
                        : "Browse the org's reusable proposal templates. Editing templates is admin-only — speak to a Super Admin to request a new one."
                }
                actions={canWrite ? (
                    <TemplateDialog onSaved={load}>
                        <Button data-testid="new-template-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                            <Plus size={16} weight="bold" /> New template
                        </Button>
                    </TemplateDialog>
                ) : null}
            />
            <div className="px-6 sm:px-8 py-8">
                {loading ? (
                    <div className="text-sm text-slate-500">Loading templates...</div>
                ) : items.length === 0 ? (
                    <EmptyState icon={<FileDoc size={40} weight="thin" />} title="No templates yet" />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="templates-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Template</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Lines</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Active</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Updated</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((t) => (
                                    <tr key={t.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`template-row-${t.id}`}>
                                        <td className="px-5 py-3">
                                            <div className="font-mono text-xs text-slate-500">{t.code}</div>
                                            <div className="font-semibold text-slate-900">{t.name}</div>
                                            {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                                        </td>
                                        <td className="px-5 py-3 text-slate-700">{t.line_items.length}</td>
                                        <td className="px-5 py-3">
                                            <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border ${t.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
                                                {t.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-500">{formatDateTime(t.updated_at)}</td>
                                        <td className="px-5 py-3 text-right">
                                            {canWrite ? (
                                                <div className="inline-flex items-center gap-1">
                                                    <TemplateDialog template={t} onSaved={load}>
                                                        <Button variant="ghost" size="sm" className="gap-1 text-slate-600" data-testid={`template-edit-${t.id}`}>
                                                            <PencilSimple size={14} /> Edit
                                                        </Button>
                                                    </TemplateDialog>
                                                    <Button variant="ghost" size="sm" onClick={() => toggleActive(t)} className={t.is_active ? "text-rose-600 gap-1" : "text-emerald-700 gap-1"} data-testid={`template-toggle-${t.id}`}>
                                                        {t.is_active ? <><ToggleRight size={14} weight="fill" /> Deactivate</> : <><ToggleLeft size={14} /> Activate</>}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <span className="text-[11px] text-slate-400 italic">Read-only</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
